import React, { useEffect, useRef, useState } from "react";
import { Button, Message } from "semantic-ui-react";
import { AdminAuth, Auth } from "../../../../api";
import { useAuth } from "../../../../hooks";

const adminAuthController = new AdminAuth();
const authController = new Auth();
const RESEND_COOLDOWN_SECONDS = 60;
const OTP_LENGTH = 6;

/**
 * Paso 3 del flujo 2FA admin: introducción del código OTP.
 */
export function AdminOtpInput({ otpToken, email, onBack, onAbort }) {
  const { login } = useAuth();
  const [digits, setDigits] = useState(Array(OTP_LENGTH).fill(""));
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputsRef = useRef([]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  useEffect(() => {
    if (inputsRef.current[0]) inputsRef.current[0].focus();
  }, []);

  const code = digits.join("");
  const complete = code.length === OTP_LENGTH && /^\d{6}$/.test(code);

  const handleChange = (idx, value) => {
    const v = value.replace(/\D/g, "").slice(0, 1);
    const next = [...digits];
    next[idx] = v;
    setDigits(next);
    setError(null);
    if (v && idx < OTP_LENGTH - 1) {
      inputsRef.current[idx + 1]?.focus();
    }
  };

  const handleKeyDown = (idx, e) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      inputsRef.current[idx - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(OTP_LENGTH).fill("");
    for (let i = 0; i < pasted.length; i += 1) next[i] = pasted[i];
    setDigits(next);
    const focusIdx = Math.min(pasted.length, OTP_LENGTH - 1);
    inputsRef.current[focusIdx]?.focus();
  };

  const handleVerify = async () => {
    if (!complete) return;
    setVerifying(true);
    setError(null);
    setInfo(null);
    try {
      const { access, refresh } = await adminAuthController.verifyOtp(
        otpToken,
        code,
      );
      authController.setAccessToken(access);
      authController.setRefreshToken(refresh);
      login(access);
    } catch (err) {
      const msg = err.message || "Error verificando el código.";
      setError(msg);
      setDigits(Array(OTP_LENGTH).fill(""));
      inputsRef.current[0]?.focus();
      if (err.code === 410) {
        onBack();
      } else if (err.code === 401 && err.data == null) {
        onAbort();
      }
    } finally {
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    setError(null);
    setInfo(null);
    try {
      await adminAuthController.resendOtp(otpToken);
      setInfo("Código reenviado. Revisa tu bandeja.");
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err) {
      setError(err.message || "No se pudo reenviar el código.");
    }
  };

  const maskedEmail = (() => {
    if (!email) return "";
    const [local, domain] = email.split("@");
    if (!domain) return email;
    if (local.length <= 2) return `${local[0]}***@${domain}`;
    return `${local[0]}***${local[local.length - 1]}@${domain}`;
  })();

  return (
    <div className="auth-step">
      <h2 className="auth-step__title">Introduce el código</h2>
      <p className="auth-step__subtitle">
        Hemos enviado un código de 6 dígitos a <strong>{maskedEmail}</strong>.
        Caduca en 10 minutos.
      </p>

      {error && (
        <Message negative size="small">
          {error}
        </Message>
      )}
      {info && (
        <Message positive size="small">
          {info}
        </Message>
      )}

      <div className="auth-otp" onPaste={handlePaste}>
        {digits.map((d, idx) => (
          <input
            // eslint-disable-next-line react/no-array-index-key
            key={idx}
            ref={(el) => {
              inputsRef.current[idx] = el;
            }}
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            aria-label={`Dígito ${idx + 1} del código`}
            maxLength={1}
            value={d}
            onChange={(e) => handleChange(idx, e.target.value)}
            onKeyDown={(e) => handleKeyDown(idx, e)}
            disabled={verifying}
          />
        ))}
      </div>

      <Button
        type="button"
        primary
        fluid
        loading={verifying}
        disabled={!complete || verifying}
        onClick={handleVerify}
      >
        Verificar y entrar
      </Button>

      <Button
        type="button"
        basic
        fluid
        style={{ marginTop: 10 }}
        disabled={cooldown > 0 || verifying}
        onClick={handleResend}
      >
        {cooldown > 0
          ? `Reenviar en 0:${String(cooldown).padStart(2, "0")}`
          : "Reenviar código"}
      </Button>

      <hr className="auth-step__divider" />

      <Button type="button" basic fluid onClick={onBack} disabled={verifying}>
        Cambiar correo de verificación
      </Button>
    </div>
  );
}
